import { useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import { CHART_TYPES, fetchChartSvg, getAllPlanetData, getAllHouseData, vargaShortKey, LAGNA_POLYGON } from '@astro/vedastro'
import { ksSelectStyles, PillGroup, StudioError, ChartSkeleton } from '../StudioBits'
import {
  NUMBER_FONT_SIZE, NUMBER_FILL, houseGlyphs, chartLayoutData, buildChartLayout, stripPlanetLayer, buildNumberedSvg,
} from '../chartSvg'

const CHART_OPTIONS = CHART_TYPES.map((ct) => ({ value: ct.key, label: `${ct.short} · ${ct.name}` }))
const QUICK_PICKS = [
  { key: 'RasiD1', label: 'Lagna D1' },
  { key: 'NavamshaD9', label: 'Navamsha D9' },
  { key: 'DashamamshaD10', label: 'Career D10' },
]
const STYLE = 'North'

/**
 * Divisional-chart viewer — all 16 vargas, drawn live by VedAstro's own
 * NorthIndianChart renderer for the grid, with rashi numbers and classical
 * two-letter planet codes overlaid on top (VedAstro's own SVG omits both —
 * it only labels houses with full planet names). The overlay's house/rashi
 * math is computed locally in `northIndianChart.js`, verified against
 * VedAstro's own AllPlanetData output before shipping (see that file's
 * doc comment) — for vargas without a verified formula (D2, D7, D30, D60)
 * we fall back to VedAstro's plain, unnumbered chart rather than guess.
 */
export default function ChartsPart({ birth }) {
  const [type, setType] = useState('RasiD1')
  const [status, setStatus] = useState('loading') // loading | error | done
  const [svg, setSvg] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [layoutData, setLayoutData] = useState(null) // { planets, vargas, ascendantLongitude }
  // Whether the rashi-number fetch has finished — settled, not necessarily
  // successful. The first paint waits on this so the chart never renders
  // un-numbered and then visibly redraws once the numbers land; a failure
  // still releases it, falling back to VedAstro's own plain chart.
  const [dataSettled, setDataSettled] = useState(false)
  const reqRef = useRef(0)
  const dataReqRef = useRef(0)

  const meta = CHART_TYPES.find((c) => c.key === type)
  const shortKey = vargaShortKey(type)

  const load = () => {
    const my = ++reqRef.current
    setStatus('loading')
    fetchChartSvg({ dob: birth.dob, tob: birth.tob, location: birth.location, chartType: type, style: STYLE })
      .then((markup) => {
        if (my !== reqRef.current) return
        setSvg(markup)
        setStatus('done')
      })
      .catch((err) => {
        if (my !== reqRef.current || err?.name === 'AbortError') return
        setErrorMsg(err?.message || 'We could not draw this chart right now. Please try again.')
        setStatus('error')
      })
  }
  useEffect(load, [birth, type]) // eslint-disable-line react-hooks/exhaustive-deps

  // Rashi-number overlay data — independent of the selected chart type (one
  // planet table + one house table cover every varga), fetched once per
  // birth. Best-effort: a failure just falls back to VedAstro's plain SVG
  // rather than blocking the chart on a second paid call.
  useEffect(() => {
    const my = ++dataReqRef.current
    setLayoutData(null)
    setDataSettled(false)
    const args = { dob: birth.dob, tob: birth.tob, location: birth.location }
    Promise.all([getAllPlanetData(args), getAllHouseData(args)])
      .then(([planets, houses]) => {
        if (my !== dataReqRef.current) return
        setLayoutData(chartLayoutData(planets, houses))
      })
      .catch(() => {})
      .finally(() => {
        if (my === dataReqRef.current) setDataSettled(true)
      })
  }, [birth]) // eslint-disable-line react-hooks/exhaustive-deps

  const layout = useMemo(() => buildChartLayout(layoutData, shortKey), [layoutData, shortKey])
  const gridOnlySvg = useMemo(() => stripPlanetLayer(svg), [svg])
  // What's on screen: VedAstro's grid + our numbered layer when available,
  // else VedAstro's own plain SVG unchanged. Downloads must match what's shown.
  const numberedSvg = useMemo(() => buildNumberedSvg(gridOnlySvg, layout), [layout, gridOnlySvg])

  const download = () => {
    const markup = numberedSvg || svg
    if (!markup) return
    const blob = new Blob([markup], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${(birth.name.trim() || 'kundli').replace(/\s+/g, '-').toLowerCase()}-${meta?.short || type}-${STYLE.toLowerCase()}.svg`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <div className="ks-controls">
        <PillGroup ariaLabel="Quick charts" options={QUICK_PICKS} value={type} onChange={setType} />
        <div className="ks-chart-select">
          <Select
            aria-label="Divisional chart"
            options={CHART_OPTIONS}
            value={CHART_OPTIONS.find((o) => o.value === type)}
            onChange={(o) => o && setType(o.value)}
            styles={ksSelectStyles}
            isSearchable={false}
          />
        </div>
      </div>

      {status === 'error' ? (
        <StudioError text={errorMsg} onRetry={load} />
      ) : svg && dataSettled ? (
        <>
          <div className="ks-chart-stage">
            <div className={`ks-chart-frame${status === 'loading' ? ' ks-chart-loading' : ''}`}>
              {layout ? (
                <div className="ks-chart-canvas">
                  <div dangerouslySetInnerHTML={{ __html: gridOnlySvg }} />
                  <svg className="ks-chart-numbers-overlay" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
                    <polygon points={LAGNA_POLYGON} fill={NUMBER_FILL} opacity="0.08" />
                    {layout.houses.map((h) => {
                      const { number, planets } = houseGlyphs(h)
                      return (
                        <g key={h.house}>
                          <text
                            x={number.x}
                            y={number.y}
                            textAnchor={number.anchor}
                            fontSize={NUMBER_FONT_SIZE}
                            fontWeight="700"
                            fill={NUMBER_FILL}
                          >
                            {number.text}
                          </text>
                          {planets.map((p) => (
                            <g key={p.key}>
                              <text
                                x={p.x}
                                y={p.y}
                                textAnchor="middle"
                                fontSize={p.fontSize}
                                fontWeight="600"
                                fill={p.fill}
                              >
                                {p.text}
                              </text>
                              {p.deg && (
                                <text
                                  x={p.deg.x}
                                  y={p.deg.y}
                                  textAnchor="start"
                                  fontSize={p.deg.fontSize}
                                  fontWeight="600"
                                  fill={p.fill}
                                >
                                  {p.deg.text}
                                </text>
                              )}
                            </g>
                          ))}
                        </g>
                      )
                    })}
                  </svg>
                </div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: svg }} />
              )}
            </div>
            {status === 'loading' && (
              <div className="ks-chart-overlay" role="status" aria-live="polite">
                <span className="ks-spinner" />
                <p>Drawing {meta?.short} · {meta?.name}…</p>
              </div>
            )}
          </div>
          <div className="ks-chart-meta">
            <p>
              <strong>{meta?.short} · {meta?.name}</strong> — {meta?.about}
            </p>
            <button type="button" className="ks-dl" onClick={download}>
              ⬇ Download SVG
            </button>
          </div>
        </>
      ) : (
        <ChartSkeleton />
      )}
    </div>
  )
}
